import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import z from "zod";

import { CandidacyStatusSelectContent, Column, Text } from "@/components";
import { ErrorText } from "@/components/ErrorText";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormSwitchInput } from "@/components/form/FormSwitchInput";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectTrigger, SelectValue } from "@/components/ui/select";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useMoveCandidacy } from "@/hooks/mutations/useMoveCandidacy";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyStatus, JobStatus } from "@/types";

const formSchema = z.object({
  agentShouldReachOut: z.boolean(),
  companyId: z.string().min(1, "Required"),
  jobId: z.string().min(1, "Required"),
  status: z.nativeEnum(CandidacyStatus),
});

type FormSchema = z.infer<typeof formSchema>;

export const MoveCandidacyDialog = ({
  agentCurrentlyActive,
  candidacyId,
  currentJobId,
  currentStatus,
  hasSignedUp,
  onOpenChange,
  onSuccess,
  open,
}: {
  agentCurrentlyActive: boolean;
  candidacyId: string;
  currentJobId: string;
  currentStatus: CandidacyStatus;
  hasSignedUp: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  open: boolean;
}) => {
  /* React Hooks */
  const navigate = useNavigate();
  const successToast = useSuccessToast();
  const companies = useGetCachedCompaniesSummary();
  const {
    mutate: moveCandidacy,
    isPending,
    isError,
    error,
  } = useMoveCandidacy();

  const form = useForm<FormSchema>({
    defaultValues: {
      agentShouldReachOut: false,
      companyId: "",
      jobId: "",
      status: currentStatus,
    },
    resolver: zodResolver(formSchema),
  });

  const companyId = useWatch({ control: form.control, name: "companyId" });
  const jobId = useWatch({ control: form.control, name: "jobId" });
  const status = useWatch({ control: form.control, name: "status" });

  /* Derived Values */
  const companiesWithJobs = companies.filter(({ jobs }) => jobs.length > 0);
  const company = companiesWithJobs.find(({ id }) => id === companyId);

  // Exclude the current job and closed jobs — there's nothing to move to there.
  const filteredJobs = company?.jobs.filter(
    (job) => job.id !== currentJobId && job.status !== JobStatus.CLOSED,
  );
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const jobs = filteredJobs || [];

  const selectedJob = jobs.find((job) => job.id === jobId);

  // The agent only reaches out on SHORTLIST, so only surface the choice when
  // the candidacy is landing there on a job that has the agent enabled
  const showAgentPrompt =
    !agentCurrentlyActive &&
    status === CandidacyStatus.SHORTLIST &&
    !!selectedJob?.stafferAgentActive;

  const companyOptions = companiesWithJobs.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  const jobOptions = jobs.map((job) => ({
    value: job.id,
    label: job.title,
  }));

  /* Handlers */
  const handleFormSubmit = (data: FormSchema) => {
    mixpanelUtils.trackFormSubmission(
      "Move candidacy submit",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );

    moveCandidacy(
      {
        candidacyId,
        fromStatus: currentStatus,
        moveCandidacyRequest: {
          agentShouldReachOut: showAgentPrompt
            ? data.agentShouldReachOut
            : undefined,
          jobId: data.jobId,
          status: data.status,
        },
      },
      {
        onSuccess: () => {
          form.reset();
          onSuccess();
          successToast({ description: "Candidacy moved!" });
          // The candidacyId is unchanged — just route to the destination job's
          // pipeline with this candidacy's sheet open.
          void navigate(
            `/companies/${data.companyId}/jobs/${data.jobId}?c=${candidacyId}`,
          );
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Dialog
        onOpenChange={(open) => {
          form.reset();
          onOpenChange(open);
        }}
        open={open}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-slate-700">
              Move Candidacy
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex min-w-0 flex-col gap-4 overflow-hidden"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <FormEnumSelect
                fieldName="companyId"
                form={form}
                label="Select a company"
                onChange={() => {
                  form.setValue("jobId", "");
                }}
                options={companyOptions}
                placeholder="Select a company"
              />
              <FormEnumSelect
                fieldName="jobId"
                form={form}
                label="Select a job"
                options={jobOptions}
                placeholder="Select a job"
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <CandidacyStatusSelectContent />
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {agentCurrentlyActive && (
                <Column className="gap-1 rounded-md bg-amber-50 p-3">
                  <Text className="text-sm font-medium text-amber-700">
                    AI scheduling agent is currently in contact with this
                    candidate.
                  </Text>
                  <Text className="text-sm font-normal text-amber-700">
                    Moving them will stop that outreach and hand all
                    communication over to you. The agent won&apos;t pick the
                    conversation back up on the new job.
                  </Text>
                </Column>
              )}
              {showAgentPrompt && (
                <Column className="gap-1">
                  <FormSwitchInput
                    fieldName="agentShouldReachOut"
                    form={form}
                    label="Have Stevie reach out"
                  />
                  <Text className="text-sm font-normal text-slate-500">
                    This job has the scheduling agent on. Moving the candidate
                    to Shortlist will let Stevie reach out to them
                    automatically.
                  </Text>
                </Column>
              )}
              {hasSignedUp && (
                <Column className="gap-1 rounded-md bg-blue-50 p-3">
                  <Text className="text-sm font-medium text-blue-700">
                    The candidate won&apos;t be notified about this move.
                  </Text>
                  <Text className="text-sm font-normal text-blue-700">
                    However, this candidate has signed up with Laborup and may
                    notice the job change. Consider giving them a heads up so
                    they aren&apos;t confused.
                  </Text>
                </Column>
              )}
              <Column className="items-end gap-2">
                <Button
                  className="w-16 rounded-full"
                  disabled={isPending}
                  type="submit"
                >
                  {isPending ? (
                    <LoadingIndicator gearColor="white" variant="small" />
                  ) : (
                    <span>Move</span>
                  )}
                </Button>
                {isError && (
                  <ErrorText
                    errorMessage={
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      error.response?.data.message ||
                      "An error occurred while moving the candidacy. Please try again."
                    }
                  />
                )}
              </Column>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
